import { useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

type SignIn = { customToken: string; result: Promise<void> } | null;

/**
 * Signs in with the custom token the backend returns for a confirmed
 * verification link, through AuthContext's `loginWithCustomToken`. At most
 * once per token per mount — StrictMode runs effects twice in development,
 * and a second sign-in would upsert the user twice. A failure is not
 * retried: the caller falls back to "Go to sign in".
 *
 * The returned function is stable, so an effect can depend on it.
 */
export const useCustomTokenSignIn = () => {
    const { loginWithCustomToken } = useAuth();
    const latestLogin = useRef(loginWithCustomToken);
    latestLogin.current = loginWithCustomToken;
    const signIn = useRef<SignIn>(null);

    return useCallback((customToken: string): Promise<void> => {
        if (signIn.current?.customToken === customToken) return signIn.current.result;
        const result = latestLogin.current(customToken);
        signIn.current = { customToken, result };
        return result;
    }, []);
};
